import {Injectable} from '@angular/core';
import {DailyProgress, DailyStats} from "./daily.model";
import {applyResult, getCurrentStreak, getGermanDateString} from "./daily.util";

const PROGRESS_KEY = 'evergreens.daily.progress';
const STATS_KEY = 'evergreens.daily.stats';
const MAX_STORED_DAYS = 30;

const initialStats: DailyStats = {
  currentStreak: 0,
  bestStreak: 0,
  played: 0,
  wins: 0
};

/**
 * Stores the progress and statistics of the daily challenge in the local storage
 * so the challenge can only be played once per day on the same device.
 */
@Injectable({
  providedIn: 'root'
})
export class DailyStorageService {

  getProgress(date: string): DailyProgress {
    return this.readAllProgress()[date] ?? {date, results: [], finished: false};
  }

  saveProgress(progress: DailyProgress) {
    const allProgress = this.readAllProgress();
    allProgress[progress.date] = progress;
    // only keep the most recent days
    Object.keys(allProgress).sort().reverse()
      .slice(MAX_STORED_DAYS)
      .forEach(date => delete allProgress[date]);
    this.write(PROGRESS_KEY, allProgress);
  }

  /**
   * Stores the final result and updates the winning streak
   */
  finish(progress: DailyProgress, won: boolean): DailyStats {
    let stats = this.getStats();
    if (!this.getProgress(progress.date).finished) {
      stats = applyResult(stats, progress.date, won);
      this.write(STATS_KEY, stats);
    }
    this.saveProgress({...progress, finished: true});
    return stats;
  }

  getStats(): DailyStats {
    return {...initialStats, ...this.read<DailyStats>(STATS_KEY)};
  }

  getCurrentStreak(today: string = getGermanDateString()): number {
    return getCurrentStreak(this.getStats(), today);
  }

  private readAllProgress(): Record<string, DailyProgress> {
    return this.read<Record<string, DailyProgress>>(PROGRESS_KEY) ?? {};
  }

  private read<T>(key: string): T | undefined {
    try {
      const value = localStorage.getItem(key);
      return value ? JSON.parse(value) : undefined;
    } catch {
      return undefined;
    }
  }

  private write(key: string, value: any) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (err) {
      console.error('Failed to store daily challenge', err);
    }
  }
}
